import { useState } from "react";
import { Check, KeyRound, Minus, X } from "lucide-react";
import { answerKeyFor, reviewFor, type KeyedQuestion, type TheirFile } from "@/lib/answerKey";
import "./ResponseReview.css";

/**
 * One learner answer, shown the way the question was asked.
 *
 * The grader used to print the answer and the key side by side as two lists of
 * text. That is unreadable on anything with parts: five pairs beside five other
 * pairs, and the teacher diffs them by eye to find the one that is wrong. Here
 * the answer keeps its shape, the wrong row is coloured, and the key is the
 * other face of the same panel — one flip away, never competing for the space.
 *
 * The key is on a flip rather than a permanent column for a second reason: what
 * a marker needs first is what the child wrote. The key is a check they reach
 * for on the ones that look wrong.
 */

function isImageFile(file: TheirFile) {
  if (file.mime && file.mime.startsWith("image/")) return true;
  return /\.(png|jpe?g|gif|webp|svg)$/i.test(file.name || file.url || "");
}

function StateMark({ state }: { state: "right" | "wrong" | "unknown" }) {
  if (state === "right") return <Check size={13} strokeWidth={2.5} aria-label="Correct" />;
  if (state === "wrong") return <X size={13} strokeWidth={2.5} aria-label="Not correct" />;
  return <Minus size={13} strokeWidth={2} aria-hidden="true" />;
}

function Blank() {
  return <span className="al-rev-blank">No answer</span>;
}

export default function ResponseReview({
  question,
  answer,
  verdict,
  details,
  badges,
}: {
  question: KeyedQuestion;
  answer: unknown;
  verdict: boolean | null;
  details?: string;
  badges?: { key: string; label: string }[];
}) {
  const [showKey, setShowKey] = useState(false);
  const review = reviewFor(question, answer, verdict);
  const key = answerKeyFor(question);

  if (review.kind === "none") {
    return <p className="al-rev-note">Directions only — nothing to mark.</p>;
  }

  const canFlip = review.keyKnown || key.lines.length > 0;
  const open = showKey && canFlip;

  /** "3 of 5 matched" — the thing a teacher counts by hand otherwise. */
  let tally = "";
  if (review.kind === "pairs" && review.keyKnown && review.rows.length > 1) {
    const right = review.rows.filter((row) => row.state === "right").length;
    tally = `${right} of ${review.rows.length} matched`;
  }
  if (review.kind === "fields" && review.keyKnown && review.rows.length > 1) {
    const right = review.rows.filter((row) => row.state === "right").length;
    tally = `${right} of ${review.rows.length} blanks right`;
  }

  return (
    <section className={`al-rev${open ? " is-key" : ""}`}>
      <header className="al-rev-head">
        {canFlip ? (
          <div className={`al-rev-flip${open ? " is-key" : ""}`}>
            <span className="al-rev-flip-thumb" aria-hidden="true" />
            <button
              type="button"
              className={open ? "" : "is-on"}
              aria-pressed={!open}
              onClick={() => setShowKey((on) => !on)}
            >
              Response
            </button>
            <button
              type="button"
              className={open ? "is-on" : ""}
              aria-pressed={open}
              onClick={() => setShowKey((on) => !on)}
            >
              <KeyRound size={12} aria-hidden="true" />
              Key
            </button>
          </div>
        ) : (
          <h3>Response</h3>
        )}
        {badges?.length ? (
          <span className="al-rev-kinds">
            {badges.map((flag) => (
              <span key={flag.key} className={`al-rev-kind al-rev-kind--${flag.key}`}>
                {flag.label}
              </span>
            ))}
          </span>
        ) : null}
        {details && !open ? <span className="al-rev-details">{details}</span> : null}
        {tally && !open ? <span className="al-rev-tally">{tally}</span> : null}
        {open ? <span className="al-rev-tag">{key.label}</span> : null}
        {!canFlip && !details ? <span className="al-rev-tag">{key.label}</span> : null}
      </header>

      {review.kind === "options" ? (
        <ul className="al-rev-options">
          {review.rows.map((row) => {
            const state = open
              ? row.expected
                ? "key"
                : "idle"
              : row.chosen
                ? review.keyKnown
                  ? row.expected
                    ? "right"
                    : "wrong"
                  : "picked"
                : "idle";
            return (
              <li key={row.id} className={`al-rev-option is-${state}`}>
                <span className="al-rev-option-mark">
                  {state === "wrong" ? (
                    <X size={13} strokeWidth={2.5} aria-hidden="true" />
                  ) : state === "idle" ? null : (
                    <Check size={13} strokeWidth={2.5} aria-hidden="true" />
                  )}
                </span>
                <span className="al-rev-option-label">{row.label}</span>
                {!open && row.chosen ? <span className="al-rev-pill">Picked</span> : null}
                {open && row.expected ? <span className="al-rev-pill">Key</span> : null}
              </li>
            );
          })}
        </ul>
      ) : null}

      {review.kind === "pairs" ? (
        <ul className="al-rev-pairs">
          {review.rows.map((row) => {
            const value = open ? row.expected : row.theirs;
            return (
              <li key={row.id} className={`al-rev-pair is-${open ? "key" : row.state}`}>
                <span className="al-rev-pair-mark">
                  {open ? <KeyRound size={12} aria-hidden="true" /> : <StateMark state={row.state} />}
                </span>
                <span className="al-rev-pair-left">{row.left}</span>
                <span className="al-rev-pair-arrow" aria-hidden="true">
                  →
                </span>
                <span className="al-rev-pair-right">{value || <Blank />}</span>
              </li>
            );
          })}
        </ul>
      ) : null}

      {review.kind === "fields" ? (
        <ul className="al-rev-fields">
          {review.rows.map((row, i) => {
            const value = open ? row.expected : row.theirs;
            return (
              <li
                key={`${row.label || "answer"}-${i}`}
                className={`al-rev-field is-${open ? "key" : row.state}`}
              >
                {!open && row.state !== "unknown" ? (
                  <span className="al-rev-field-mark">
                    <StateMark state={row.state} />
                  </span>
                ) : null}
                <span className="al-rev-field-body">
                  {row.label ? <span className="al-rev-field-label">{row.label}</span> : null}
                  <span className="al-rev-field-value">{value || <Blank />}</span>
                </span>
              </li>
            );
          })}
        </ul>
      ) : null}

      {review.kind === "files" ? (
        review.files.length ? (
          <ul className="al-rev-files">
            {review.files.map((file, i) => (
              <li key={`${file.url || file.name}-${i}`}>
                {file.url ? (
                  <a href={file.url} target="_blank" rel="noreferrer">
                    {isImageFile(file) ? (
                      <img src={file.url} alt="" className="al-rev-thumb" />
                    ) : null}
                    {file.name}
                  </a>
                ) : (
                  file.name
                )}
              </li>
            ))}
          </ul>
        ) : (
          <p className="al-rev-empty">
            <Blank />
          </p>
        )
      ) : null}

      {/* The caveat rides with the key, or stands alone when there is no key
          to flip to — "the author left this to you" is the whole brief. */}
      {key.note && (open || !review.keyKnown) ? (
        <p className="al-rev-note">{key.note}</p>
      ) : null}
    </section>
  );
}
